/*******************************************************************************
 * Copyright 2026 Collaterate. All rights reserved.
 *******************************************************************************/

// Storefront behavior for the facets and applied-filters bones.
//
// 1. Canonical address bar. After the interpreter applied values the server renders the equivalent URL in
//    data-canonical-query. Replacing the address bar (no reload, no request) makes the shared link carry the
//    f. parameters and the ix=1 settled marker, so a reload does not interpret a second time. Only a query
//    string starting "?k=" on the CURRENT path is accepted, so a bad attribute cannot point history anywhere else.
// 2. Tree branches. The server renders every branch expanded so a shopper without JavaScript can reach every
//    value; branches that hold no selection are collapsed here.
// 3. Dropdowns. The server renders each expanded; they are collapsed here, toggled by their button, and
//    closed by Escape (focus returns to the button) or a click outside.
// 4. Space activates a checkbox, switch or pressed-button facet link, as it does on a native control.
// 5. Two events, raised the way SEARCH_FACET_SELECTED is: the window.events bus for theme controllers, and a
//    collaterate:<value> CustomEvent beside it carrying the detail, which is ids and counts only, never the
//    query text. SEARCH_FACETS_LOADED fires once per facets container after the enhancement above, and
//    SEARCH_INTERPRETATION_APPLIED fires once after the address bar was actually rewritten.
// 6. The shopper's place survives the reload. Every facet, chip, Clear all, "Narrow to" and pager control is a
//    plain link, so each click is a full page load that would land at the top with focus on <body>. A click
//    records what was activated in sessionStorage; the page it lands on puts focus back on the same control (or
//    the nearest thing that stands for it) and restores the scroll. The record is used once, only by the page
//    the link pointed to, and only within RETURN_TTL_MS, so Back, a new tab or a later visit never act on it.
// 7. The phone disclosure. Below PANEL_QUERY the groups collapse behind a "Filters (N)" button, so the first product
//    is not a screen of facets away. The panel stays open when the shopper just used it (a facet return record), and
//    follows the screen when it crosses the breakpoint. Without matchMedia, or above it, nothing changes.
//
// Collapsed means the platform's hideIt class on the list, with aria-expanded as the state. The hidden
// attribute is not used: it is a third visibility mechanism a theme's hideIt rules know nothing about.
//
// No events for branch or dropdown open and close: they are internal render steps, and aria-expanded is the contract.
//
// ES5 on purpose, matching the rest of this bundle's browser targets.

(function (window, document) {
    'use strict';

    var HIDDEN_CLASS = 'hideIt';
    var RETURN_KEY = 'collaterate.searchFacets.return';
    var RETURN_TTL_MS = 30000;
    var PANEL_QUERY = '(max-width: 47.99em)';

    function logSuppressed(error) {
        try {
            if (window.console && window.console.debug) {
                window.console.debug('searchFacets suppressed an error', error);
            }
        } catch (ignored) {
            // A logging failure must not become a page failure.
        }
    }

    /**
     * Raises one event as a pair: the bus for theme controllers already listening on it (no arguments),
     * and a collaterate:<value> CustomEvent for the detail.
     *
     * Guarded on the CONSTANT, not just the bus: trigger() is gated on isEventRegistered, so a name
     * templateHeader.js never declared is a silent no-op, and dispatching a DOM event for an event that
     * cannot fire on the bus would be worse than silence. A page whose theme never ran
     * [[ template_header ]] has no bus, and raises neither.
     */
    function announce(constantName, eventName, detail) {
        try {
            if (!(window.events && window.events[constantName])) {
                return;
            }
            window.events.trigger(window.events[constantName]);
            if (typeof window.CustomEvent === 'function') {
                window.dispatchEvent(new window.CustomEvent(eventName, { detail: detail }));
            }
        } catch (error) {
            // A theme's listener throwing must not break the facets.
            logSuppressed(error);
        }
    }

    /** How many f. parameters a canonical query carries. A count, never the values. */
    function countFilterParameters(query) {
        var parts = query.substring(1).split('&');
        var count = 0;
        for (var i = 0; i < parts.length; i++) {
            if (parts[i].indexOf('f.') === 0) {
                count++;
            }
        }
        return count;
    }

    /** True for a key the canonical query owns: k, cat, band, ix and any f.* key. */
    function isOwnedKey(rawKey) {
        var key = rawKey;
        try {
            key = decodeURIComponent(rawKey);
        } catch (ignored) {
            // An undecodable key is compared as written.
        }
        return key === 'k' || key === 'cat' || key === 'band' || key === 'ix' || key.indexOf('f.') === 0;
    }

    /**
     * The parameters of the current URL the canonical query does not own (utm_*, gclid and so on), in their
     * original order, as "&a=b&c=d" or "". Analytics landing parameters must survive the rewrite.
     */
    function foreignParameters() {
        var search = window.location && window.location.search;
        if (!search || search.charAt(0) !== '?') {
            return '';
        }
        var parts = search.substring(1).split('&');
        var kept = '';
        for (var i = 0; i < parts.length; i++) {
            if (parts[i] === '') {
                continue;
            }
            var eq = parts[i].indexOf('=');
            if (!isOwnedKey(eq < 0 ? parts[i] : parts[i].substring(0, eq))) {
                kept += '&' + parts[i];
            }
        }
        return kept;
    }

    /**
     * Rewrites the address bar to the canonical query.
     *
     * @returns {number} how many f. parameters it carries when the address bar was rewritten, or -1 when it was not
     */
    function canonicalize() {
        var holder = document.querySelector('[data-canonical-query]');
        if (!holder) {
            return -1;
        }
        var query = holder.getAttribute('data-canonical-query');
        if (!query || query.indexOf('?k=') !== 0) {
            return -1;
        }
        if (!window.history || typeof window.history.replaceState !== 'function') {
            return -1;
        }
        var pathname = window.location && window.location.pathname;
        // A protocol-relative path would make the new URL cross-origin.
        if (!pathname || pathname.charAt(0) !== '/' || pathname.indexOf('//') === 0) {
            return -1;
        }
        var hash = window.location.hash || '';
        window.history.replaceState(null, '', pathname + query + foreignParameters() + hash);
        return countFilterParameters(query);
    }

    function setExpanded(button, expanded) {
        var id = button.getAttribute('aria-controls');
        var list = id ? document.getElementById(id) : null;
        button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        if (list) {
            if (expanded) {
                list.classList.remove(HIDDEN_CLASS);
            } else {
                list.classList.add(HIDDEN_CLASS);
            }
        }
    }

    function bindToggle(button) {
        var node = button.parentNode;
        var open = !!node && !!node.getAttribute && node.getAttribute('data-facet-open') === '1';
        setExpanded(button, open);
        button.addEventListener('click', function () {
            try {
                setExpanded(button, button.getAttribute('aria-expanded') !== 'true');
            } catch (error) {
                logSuppressed(error);
            }
        });
    }

    function initTrees() {
        var buttons = document.querySelectorAll('[data-facet-toggle]');
        for (var i = 0; i < buttons.length; i++) {
            bindToggle(buttons[i]);
        }
    }

    function dropdownButtons() {
        return document.querySelectorAll('[data-facet-dropdown-toggle]');
    }

    function closeDropdowns(except) {
        var buttons = dropdownButtons();
        for (var i = 0; i < buttons.length; i++) {
            if (buttons[i] !== except && buttons[i].getAttribute('aria-expanded') === 'true') {
                setExpanded(buttons[i], false);
            }
        }
    }

    function bindDropdown(button) {
        // Always starts collapsed: the button text already says how many values are selected.
        setExpanded(button, false);
        button.addEventListener('click', function () {
            try {
                var open = button.getAttribute('aria-expanded') !== 'true';
                closeDropdowns(button);
                setExpanded(button, open);
            } catch (error) {
                logSuppressed(error);
            }
        });
    }

    function initDropdowns() {
        var buttons = dropdownButtons();
        for (var i = 0; i < buttons.length; i++) {
            bindDropdown(buttons[i]);
        }
    }

    function insideDropdown(node) {
        while (node) {
            if (node.getAttribute && node.getAttribute('data-facet-dropdown') !== null) {
                return true;
            }
            node = node.parentNode;
        }
        return false;
    }

    function insideBrowse(node) {
        while (node) {
            if (node.getAttribute && node.getAttribute('data-collaterate-browse') !== null) {
                return true;
            }
            node = node.parentNode;
        }
        return false;
    }

    /** One SEARCH_FACETS_LOADED per facets container, search page and browse page alike. */
    function announceLoaded() {
        var containers = document.querySelectorAll('[data-collaterate-search-facets]');
        for (var i = 0; i < containers.length; i++) {
            announce('SEARCH_FACETS_LOADED', 'collaterate:searchFacetsLoaded', {
                mode: insideBrowse(containers[i]) ? 'browse' : 'search',
                groupCount: containers[i].querySelectorAll('[data-facet-display]').length,
                selectedCount: containers[i].querySelectorAll('[data-facet-selected="true"]').length
            });
        }
    }

    function storage() {
        try {
            return window.sessionStorage || null;
        } catch (error) {
            // Blocked storage throws on access; the page then simply reloads at the top, as before.
            return null;
        }
    }

    /** Which control a link is, as a record the next page can find it by, or null for any other link. */
    function describe(link) {
        var attr = function (name) {
            return link.getAttribute(name);
        };
        if (attr('data-facet-dimension') !== null && attr('data-facet-value') !== null) {
            return { kind: 'facet', group: attr('data-facet-dimension'), value: attr('data-facet-value') };
        }
        if (attr('data-chip-group') !== null) {
            var chips = document.querySelectorAll('[data-chip-group]');
            for (var i = 0; i < chips.length; i++) {
                if (chips[i] === link) {
                    return { kind: 'chip', index: i };
                }
            }
            return { kind: 'chip', index: 0 };
        }
        if (attr('data-clear-all') !== null) {
            return { kind: 'clear' };
        }
        if (attr('data-suggestion-group') !== null) {
            return { kind: 'suggestion', group: attr('data-suggestion-group'), value: attr('data-suggestion-value') };
        }
        var className = ' ' + (link.className || '') + ' ';
        if (/ c-(search|browse)-pager-(prev|next) /.test(className)) {
            return { kind: 'pager' };
        }
        return null;
    }

    function closestLink(node) {
        while (node && node.getAttribute) {
            if (node.tagName && String(node.tagName).toUpperCase() === 'A') {
                return node;
            }
            node = node.parentNode;
        }
        return null;
    }

    function remember(event) {
        // A modified or middle click opens another tab, and a new tab inherits sessionStorage: do not let it
        // (or this page, which stays put) pick the record up.
        if (event.defaultPrevented || (event.button && event.button !== 0)
                || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
            return;
        }
        var link = closestLink(event.target);
        var record = link ? describe(link) : null;
        var store = record ? storage() : null;
        if (!store || typeof link.pathname !== 'string') {
            return;
        }
        record.to = link.pathname + (link.search || '');
        record.at = Date.now();
        record.scrollY = window.pageYOffset || 0;
        store.setItem(RETURN_KEY, JSON.stringify(record));
    }

    /** Takes this page's record, if the last click pointed here and was recent; always clears it. */
    function takeReturn() {
        var store = storage();
        if (!store) {
            return null;
        }
        var raw = store.getItem(RETURN_KEY);
        if (!raw) {
            return null;
        }
        store.removeItem(RETURN_KEY);
        var record = JSON.parse(raw);
        var here = window.location.pathname + (window.location.search || '');
        if (!record || record.to !== here || !(Date.now() - record.at < RETURN_TTL_MS)) {
            return null;
        }
        return record;
    }

    function find(selector, test) {
        var nodes = document.querySelectorAll(selector);
        for (var i = 0; i < nodes.length; i++) {
            if (test(nodes[i])) {
                return nodes[i];
            }
        }
        return null;
    }

    /**
     * The OUTERMOST collapsed list holding node, or null when every ancestor is shown. Outermost, because a
     * nested branch's toggle sits inside its parent's list: when both are collapsed, only the outer one's
     * toggle is on screen.
     */
    function collapsedAncestor(node) {
        var outermost = null;
        var parent = node.parentNode;
        while (parent && parent.classList) {
            if (parent.classList.contains(HIDDEN_CLASS)) {
                outermost = parent;
            }
            parent = parent.parentNode;
        }
        return outermost;
    }

    /** A focusable stand-in for a value inside a collapsed branch or dropdown: the button that opens it. */
    function visibleFor(node) {
        var list = node && collapsedAncestor(node);
        if (!list) {
            return node;
        }
        return find('[data-facet-toggle], [data-facet-dropdown-toggle], [data-facet-panel-toggle]', function (button) {
            return !!list.id && button.getAttribute('aria-controls') === list.id;
        });
    }

    /** Focus for a container that is not focusable by itself, without adding it to the tab order. */
    function region(node) {
        if (node && node.getAttribute('tabindex') === null) {
            node.setAttribute('tabindex', '-1');
        }
        return node;
    }

    function targetFor(record) {
        var facets = document.querySelector('[data-collaterate-search-facets]');
        if (record.kind === 'facet') {
            return visibleFor(find('[data-facet-dimension]', function (link) {
                return link.getAttribute('data-facet-dimension') === record.group
                    && link.getAttribute('data-facet-value') === record.value;
            })) || region(facets);
        }
        if (record.kind === 'chip') {
            // The chip that took the removed one's place, else the one before it, else the facets.
            var chips = document.querySelectorAll('[data-chip-group]');
            return chips.length ? chips[Math.min(record.index, chips.length - 1)] : region(facets);
        }
        if (record.kind === 'suggestion') {
            // The suggestion is now an applied filter: its chip.
            return find('[data-chip-group]', function (chip) {
                return chip.getAttribute('data-chip-group') === record.group
                    && chip.getAttribute('data-chip-value') === record.value;
            }) || region(document.querySelector('[data-collaterate-search-applied]')) || region(facets);
        }
        if (record.kind === 'clear') {
            return region(facets);
        }
        // A new page of results: the results themselves, from their top.
        return region(document.querySelector('[data-collaterate-search-results]')
            || document.querySelector('[data-collaterate-browse]'));
    }

    function inView(node) {
        if (typeof node.getBoundingClientRect !== 'function') {
            return true;
        }
        var box = node.getBoundingClientRect();
        return box.top >= 0 && box.bottom <= (window.innerHeight || 0);
    }

    function restoreReturn(record) {
        var target = record && targetFor(record);
        if (!target || typeof target.focus !== 'function') {
            return;
        }
        if (record.kind !== 'pager' && typeof window.scrollTo === 'function') {
            window.scrollTo(0, record.scrollY || 0);
        }
        target.focus({ preventScroll: true });
        if (record.kind === 'pager' && typeof target.scrollIntoView === 'function') {
            target.scrollIntoView({ block: 'start' });
        } else if (!inView(target) && typeof target.scrollIntoView === 'function') {
            // The page above the control changed height (an answer, a chip bar): bring it back into view.
            target.scrollIntoView({ block: 'nearest' });
        }
    }

    /** "Filters" plus the count of applied values in this container, for the collapsed button's label. */
    function panelCount(container) {
        var selected = container.querySelectorAll('[data-facet-selected="true"]').length;
        return selected > 0 ? ' (' + selected + ')' : '';
    }

    function setPanel(toggle, open) {
        setExpanded(toggle, open);
    }

    function initPanels(keepOpen) {
        if (typeof window.matchMedia !== 'function') {
            return;
        }
        var media = window.matchMedia(PANEL_QUERY);
        var toggles = document.querySelectorAll('[data-facet-panel-toggle]');
        var apply = function (narrow, first) {
            for (var i = 0; i < toggles.length; i++) {
                var toggle = toggles[i];
                if (narrow) {
                    toggle.classList.remove(HIDDEN_CLASS);
                    // Collapsed on arrival, open while the shopper is working in it, and never re-collapsed under
                    // them by a resize that stays narrow.
                    if (first) {
                        setPanel(toggle, !!keepOpen);
                    }
                } else {
                    toggle.classList.add(HIDDEN_CLASS);
                    setPanel(toggle, true);
                }
            }
        };
        for (var i = 0; i < toggles.length; i++) {
            (function (toggle) {
                var count = toggle.querySelector('[data-facet-panel-count]');
                var container = toggle.parentNode;
                if (count && container && container.querySelectorAll) {
                    count.textContent = panelCount(container);
                }
                toggle.addEventListener('click', function () {
                    try {
                        setPanel(toggle, toggle.getAttribute('aria-expanded') !== 'true');
                    } catch (error) {
                        logSuppressed(error);
                    }
                });
            }(toggles[i]));
        }
        apply(media.matches, true);
        var onChange = function (event) {
            try {
                apply(event.matches, false);
            } catch (error) {
                logSuppressed(error);
            }
        };
        if (typeof media.addEventListener === 'function') {
            media.addEventListener('change', onChange);
        } else if (typeof media.addListener === 'function') {
            media.addListener(onChange);
        }
    }

    function onDocumentClick(event) {
        try {
            if (!insideDropdown(event.target)) {
                closeDropdowns(null);
            }
        } catch (error) {
            logSuppressed(error);
        }
        try {
            remember(event);
        } catch (error) {
            logSuppressed(error);
        }
    }

    function onKeydown(event) {
        try {
            if (event.key === 'Escape') {
                var buttons = dropdownButtons();
                for (var i = 0; i < buttons.length; i++) {
                    if (buttons[i].getAttribute('aria-expanded') === 'true') {
                        setExpanded(buttons[i], false);
                        if (typeof buttons[i].focus === 'function') {
                            buttons[i].focus();
                        }
                        break;
                    }
                }
                return;
            }
            if (event.key !== ' ' && event.key !== 'Spacebar') {
                return;
            }
            var target = event.target;
            var role = target && target.getAttribute ? target.getAttribute('role') : null;
            var isFacetControl = (role === 'checkbox' || role === 'switch' || role === 'button')
                && target.getAttribute('data-facet-dimension') !== null;
            // Applied-filter chips are <a role="button" data-chip-group>, and Clear all is a plain link: a native
            // button activates on Space, so these must too.
            var isAppliedControl = target && target.getAttribute
                && ((role === 'button' && target.getAttribute('data-chip-group') !== null)
                    || target.getAttribute('data-clear-all') !== null);
            if ((isFacetControl || isAppliedControl) && typeof target.click === 'function') {
                event.preventDefault();
                // A held key repeats keydown: keep the page from scrolling but do not toggle again.
                if (!event.repeat) {
                    target.click();
                }
            }
        } catch (error) {
            logSuppressed(error);
        }
    }

    // The roots the facets, applied-filters and browse JSPs emit. A page with none of them (legacy search,
    // cart, checkout, any page without these tags) gets no work and no document listeners from this file.
    function hasRoot() {
        try {
            return !!(document.querySelector('[data-collaterate-search-facets]')
                || document.querySelector('[data-collaterate-search-applied]')
                || document.querySelector('[data-collaterate-browse]'));
        } catch (error) {
            return false;
        }
    }

    function init() {
        if (!hasRoot()) {
            return;
        }
        var pendingReturn = null;
        try {
            // Before canonicalize, which may rewrite the address the record is matched against.
            pendingReturn = takeReturn();
        } catch (error) {
            logSuppressed(error);
        }
        try {
            var appliedCount = canonicalize();
            // Only after replaceState returned: a throw above skips this, so the event never claims a
            // rewrite that did not happen.
            if (appliedCount > 0) {
                announce('SEARCH_INTERPRETATION_APPLIED', 'collaterate:searchInterpretationApplied', {
                    appliedCount: appliedCount
                });
            }
        } catch (error) {
            logSuppressed(error);
        }
        try {
            initTrees();
        } catch (error) {
            logSuppressed(error);
        }
        try {
            initDropdowns();
        } catch (error) {
            logSuppressed(error);
        }
        try {
            // Kept open when the shopper was just working in the filters, so the restored focus lands on the value.
            initPanels(pendingReturn && (pendingReturn.kind === 'facet' || pendingReturn.kind === 'clear'));
        } catch (error) {
            logSuppressed(error);
        }
        try {
            // After trees, dropdowns and the panel settled, so a value they collapsed is found as collapsed.
            restoreReturn(pendingReturn);
        } catch (error) {
            logSuppressed(error);
        }
        try {
            announceLoaded();
        } catch (error) {
            logSuppressed(error);
        }
        document.addEventListener('keydown', onKeydown);
        document.addEventListener('click', onDocumentClick);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
}(window, document));
